import { lazy, Suspense, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Progress,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Typography,
  message,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { reportsApi } from '../../../api/endpoints/reports';
import { downloadBlob } from '../../../api/downloadBlob';
import { PageHeader } from '../../../components/common/PageHeader';

const { RangePicker } = DatePicker;
const { Text } = Typography;

// Carga diferida: @ant-design/plots es pesado y solo se usa en esta tab.
const StatisticsCharts = lazy(() => import('../components/StatisticsCharts'));
const ActivitiesByUserChart = lazy(() => import('../components/ActivitiesByUserChart'));

interface DateRange {
  from?: string;
  to?: string;
}

function dateRangeQuery(range: DateRange): Record<string, string | undefined> {
  return { from: range.from, to: range.to };
}

function ActivitiesByUserTab(): JSX.Element {
  const [range, setRange] = useState<DateRange>({});
  const { data, isFetching } = useQuery({
    queryKey: ['report', 'activities-by-user', range],
    queryFn: () => reportsApi.activitiesByUser(range),
  });

  const handleExport = (): void => {
    downloadBlob(
      '/reports/activities-by-user.xlsx',
      'actividades-por-usuario.xlsx',
      dateRangeQuery(range),
    ).catch(() => message.error('No se pudo exportar'));
  };

  return (
    <>
      <RangeAndExport range={range} setRange={setRange} onExport={handleExport} />
      <Card size="small" title="Carga por usuario (actividades abiertas)" style={{ marginBottom: 16 }}>
        <Suspense fallback={<div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>}>
          <ActivitiesByUserChart rows={data ?? []} />
        </Suspense>
      </Card>
      <Table
        scroll={{ x: 'max-content' }}
        rowKey="userId"
        loading={isFetching}
        dataSource={data ?? []}
        pagination={false}
        columns={[
          { title: 'Usuario', dataIndex: 'userName' },
          { title: 'Creadas', dataIndex: 'created', align: 'right' },
          { title: 'Cerradas (asignadas)', dataIndex: 'closed', align: 'right' },
          { title: 'Abiertas hoy', dataIndex: 'open', align: 'right' },
        ]}
      />
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
        “Creadas” respeta el rango; “abiertas hoy” es el snapshot actual de las
        actividades sin cerrar asignadas al usuario.
      </Text>
    </>
  );
}

function SalesByStageTab(): JSX.Element {
  const [range, setRange] = useState<DateRange>({});
  const { data, isFetching } = useQuery({
    queryKey: ['report', 'sales-by-stage', range],
    queryFn: () => reportsApi.salesByStage(range),
  });

  const handleExport = (): void => {
    downloadBlob(
      '/reports/sales-by-stage.xlsx',
      'ventas-por-etapa.xlsx',
      dateRangeQuery(range),
    ).catch(() => message.error('No se pudo exportar'));
  };

  return (
    <>
      <RangeAndExport range={range} setRange={setRange} onExport={handleExport} />
      <Table
        scroll={{ x: 'max-content' }}
        rowKey="stage"
        loading={isFetching}
        dataSource={data ?? []}
        pagination={false}
        columns={[
          { title: 'Etapa', dataIndex: 'stage' },
          { title: 'Pendientes', dataIndex: 'pendingCount', align: 'right' },
          {
            title: 'Monto pendiente',
            dataIndex: 'pendingAmount',
            align: 'right',
            render: (v: number) => v.toLocaleString('es-MX', { maximumFractionDigits: 0 }),
          },
          { title: 'Ganadas', dataIndex: 'wonCount', align: 'right' },
          {
            title: 'Monto ganado',
            dataIndex: 'wonAmount',
            align: 'right',
            render: (v: number) => v.toLocaleString('es-MX', { maximumFractionDigits: 0 }),
          },
          { title: 'Perdidas', dataIndex: 'lostCount', align: 'right' },
        ]}
      />
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
        El rango aplica sobre la fecha de última actualización de la venta.
      </Text>
    </>
  );
}

function AccountsByCategoryTab(): JSX.Element {
  const { data, isFetching } = useQuery({
    queryKey: ['report', 'accounts-by-category'],
    queryFn: () => reportsApi.accountsByCategory(),
  });

  const handleExport = (): void => {
    downloadBlob('/reports/accounts-by-category.xlsx', 'cuentas-por-categoria.xlsx').catch(
      () => message.error('No se pudo exportar'),
    );
  };

  return (
    <>
      <Space style={{ marginBottom: 12, justifyContent: 'flex-end', width: '100%' }}>
        <Button icon={<DownloadOutlined />} onClick={handleExport}>
          Exportar Excel
        </Button>
      </Space>
      <Table
        scroll={{ x: 'max-content' }}
        rowKey="category"
        loading={isFetching}
        dataSource={data ?? []}
        pagination={false}
        columns={[
          { title: 'Categoría', dataIndex: 'category' },
          { title: 'Conteo', dataIndex: 'count', align: 'right' },
        ]}
      />
    </>
  );
}

function StatisticsTab(): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['report', 'stats'],
    queryFn: () => reportsApi.stats(),
  });

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Row gutter={16}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Cuentas" value={data?.totals.accounts ?? 0} loading={isLoading} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Contactos" value={data?.totals.persons ?? 0} loading={isLoading} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Proyectos" value={data?.totals.projects ?? 0} loading={isLoading} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Ventas" value={data?.totals.sales ?? 0} loading={isLoading} />
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Card size="small" title="Tasa de conversión" loading={isLoading}>
            <div style={{ textAlign: 'center' }}>
              <Progress type="dashboard" percent={data?.conversion.rate ?? 0} />
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">
                  {data?.conversion.won ?? 0} ganadas / {data?.conversion.lost ?? 0} perdidas
                </Text>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {data && (
        <Suspense
          fallback={
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Spin />
            </div>
          }
        >
          <StatisticsCharts stats={data} />
        </Suspense>
      )}
    </Space>
  );
}

function RangeAndExport({
  range,
  setRange,
  onExport,
}: {
  range: DateRange;
  setRange: (r: DateRange) => void;
  onExport: () => void;
}): JSX.Element {
  const value: [Dayjs | null, Dayjs | null] = [
    range.from ? dayjs(range.from) : null,
    range.to ? dayjs(range.to) : null,
  ];
  return (
    <Space style={{ marginBottom: 12, justifyContent: 'space-between', width: '100%' }}>
      <RangePicker
        value={value as [Dayjs, Dayjs]}
        onChange={(dates) =>
          setRange({
            from: dates?.[0]?.startOf('day').toISOString(),
            to: dates?.[1]?.endOf('day').toISOString(),
          })
        }
        format="DD/MM/YYYY"
      />
      <Button icon={<DownloadOutlined />} onClick={onExport}>
        Exportar Excel
      </Button>
    </Space>
  );
}

export function ReportsPage(): JSX.Element {
  return (
    <>
      <PageHeader title="Reportes" crumbs={[{ title: 'Reportes' }]} />
      <Card>
        <Tabs
          items={[
            {
              key: 'stats',
              label: 'Estadísticas',
              children: <StatisticsTab />,
            },
            {
              key: 'activities',
              label: 'Actividades por usuario',
              children: <ActivitiesByUserTab />,
            },
            {
              key: 'sales',
              label: 'Ventas por etapa',
              children: <SalesByStageTab />,
            },
            {
              key: 'accounts',
              label: 'Cuentas por categoría',
              children: <AccountsByCategoryTab />,
            },
          ]}
        />
      </Card>
    </>
  );
}
